import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const SearchResults = ({ 
  results = [], 
  query = '', 
  isLoading = false, 
  onResultClick,
  onClearSearch 
}) => {
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Searching for "{query}"...
        </Typography>
      </Box>
    );
  }

  if (!query) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Enter a search term to find runbooks, templates, and more
        </Typography>
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No results found for "{query}"
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Try adjusting your search terms or browse our templates instead
        </Typography>
        <Button variant="outlined" onClick={onClearSearch}>
          Clear Search
        </Button>
      </Box>
    );
  }

  const groupedResults = {
    runbooks: results.filter(result => result.type === 'runbook'),
    templates: results.filter(result => result.type === 'template'),
    categories: results.filter(result => result.type === 'category'),
    users: results.filter(result => result.type === 'user')
  };

  const renderResultItem = (result) => {
    const getIcon = (type) => {
      switch (type) {
        case 'runbook':
          return <DescriptionIcon color="primary" />;
        case 'template':
          return <CategoryIcon color="secondary" />;
        case 'category':
          return <CategoryIcon color="info" />;
        case 'user':
          return <PersonIcon color="success" />;
        default:
          return <SearchIcon color="action" />;
      }
    };

    const getTypeColor = (type) => {
      switch (type) {
        case 'runbook':
          return 'primary';
        case 'template':
          return 'secondary';
        case 'category':
          return 'info';
        case 'user':
          return 'success';
        default:
          return 'default';
      }
    };

    return (
      <Card key={result.id} variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ mt: 0.5 }}>
              {getIcon(result.type)}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" component="h3">
                  {result.title}
                </Typography>
                <Chip 
                  label={result.type} 
                  size="small" 
                  color={getTypeColor(result.type)}
                  variant="outlined"
                />
              </Box>
              
              {result.description && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {result.description}
                </Typography>
              )}

              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                {result.category && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CategoryIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {result.category}
                    </Typography>
                  </Box>
                )}
                
                {result.author && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {result.author}
                    </Typography>
                  </Box>
                )}
                
                {result.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {result.location}
                    </Typography>
                  </Box>
                )}
                
                {result.updatedAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(result.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {result.tags && result.tags.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {result.tags.map((tag, index) => (
                      <Chip 
                        key={index} 
                        label={tag} 
                        size="small" 
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
        
        <CardActions>
          <Button 
            size="small" 
            onClick={() => onResultClick(result)}
            variant="contained"
          >
            {result.type === 'runbook' ? 'View Runbook' : 
             result.type === 'template' ? 'Use Template' : 
             result.type === 'category' ? 'Browse Category' : 'View Profile'}
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box>
      {/* Search Summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Search Results for "{query}"
          </Typography>
          <Button variant="outlined" size="small" onClick={onClearSearch}>
            Clear Search
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Found {results.length} result{results.length !== 1 ? 's' : ''}
        </Typography>
      </Paper>

      {/* Results by Category */}
      <Stack spacing={3}>
        {/* Runbooks */}
        {groupedResults.runbooks.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Runbooks ({groupedResults.runbooks.length})
            </Typography>
            {groupedResults.runbooks.map(renderResultItem)}
          </Box>
        )}

        {/* Templates */}
        {groupedResults.templates.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Templates ({groupedResults.templates.length})
            </Typography>
            {groupedResults.templates.map(renderResultItem)}
          </Box>
        )}

        {/* Categories */}
        {groupedResults.categories.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Categories ({groupedResults.categories.length})
            </Typography>
            {groupedResults.categories.map(renderResultItem)}
          </Box>
        )}

        {/* Users */}
        {groupedResults.users.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Users ({groupedResults.users.length})
            </Typography>
            {groupedResults.users.map(renderResultItem)}
          </Box>
        )}
      </Stack>

      {/* Search Tips */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Search Tips
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <List dense>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Use quotes for exact phrases"
                  secondary="e.g., 'emergency procedure'"
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Search by category"
                  secondary="e.g., 'medical' or 'fire safety'"
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List dense>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Search by author"
                  secondary="e.g., 'john' or 'family admin'"
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Use tags"
                  secondary="e.g., 'urgent' or 'beginner'"
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SearchResults;